/**
 * What the assistant is told about /overview: a LookerChatContext (R18) with
 * template.id "overview", read by the bubble in mode 'overview' with
 * OVERVIEW_ADVISOR, whose page rules explain its keys (spec 2026-10-20 page
 * skills §2.3). Built from the same box tables the file writes, so the two
 * cannot drift; cells are shown as the page renders them. App boxes and the
 * page-level reads use /looker's own converters, unchanged. The context is
 * serialised on the client (agentService DASHBOARD_CONTEXT); no server code
 * reads template.id.
 *
 * @module services/overview/overviewChatContext
 */
import {
  appChatTile, backfillOfState, freshnessOfState, LOOKER_CHAT_ROW_CAP, targetFields,
  type LookerChatContext, type LookerChatTile,
} from '@/services/looker/lookerChatContext';
import type { LookerTileType } from '@/services/looker/lookerTypes';
import { shownCell } from './boxTableParts';
import { APP_BOX, OVERVIEW_LAYOUT, type BoxTable, type OverviewPageInput } from './overviewTables';

export const OVERVIEW_TEMPLATE_ID = 'overview';

const TILE_TYPE: Readonly<Record<string, LookerTileType>> = {
  2: 'single_value', 3: 'single_value', 4: 'line', 5: 'pie', 9: 'table', 11: 'table', 12: 'table', 13: 'table',
};

/** null = not told yet (loading); a failed or empty box is told as such. */
const chatTileOf = (b: BoxTable, currency: string | null): LookerChatTile | null => {
  if (b.status === 'loading' || b.status === 'needsWorkspace') return null;
  const shown = b.rows.slice(0, LOOKER_CHAT_ROW_CAP);
  const ok = b.status === 'ok';
  return {
    title: b.title,
    type: TILE_TYPE[b.id] ?? 'table',
    status: b.status,
    columns: b.columns,
    rows: shown.map((r, ri) => Object.fromEntries(b.columns.map((c, i) => [c, shownCell(b.kinds[ri]?.[i], r[i], currency)]))),
    rowsOmitted: b.rows.length - shown.length,
    moreRowsThanLoaded: false,
    ...(b.notes.length > 0 ? { coverageNotes: b.notes } : {}),
    ...(b.targets ? targetFields(b.targets) : {}),
    ...(b.resultMetric ? { resultMetric: b.resultMetric } : {}),
    ...(ok && b.resultThrough ? { resultThrough: b.resultThrough } : {}),
    ...(ok && b.resultThrough && b.resultPrevious ? { resultPrevious: { start: b.resultPrevious.start, end: b.resultPrevious.end } } : {}),
  };
};

export const buildOverviewChatContext = (input: OverviewPageInput): LookerChatContext => {
  const w = input.windows;
  const currency = input.money?.currency ?? null;
  return {
    ...backfillOfState(input.backfill),
    ...freshnessOfState(input.freshness),
    kind: 'looker',
    template: { id: OVERVIEW_TEMPLATE_ID, title: input.title },
    scope: input.allWorkspaces ? { allWorkspaces: true, workspaceName: null } : { allWorkspaces: false, workspaceName: input.workspaceName },
    filters: input.filters,
    period: { start: w.current.start, end: w.current.end, previousStart: w.previous.start, previousEnd: w.previous.end },
    latestDataDate: input.latestDataDate,
    currency,
    // As the page's currency line, with no currency too: then no account that spent has one, so every facts box is empty.
    ...(input.money?.unknownExcluded ? { currencyUnknownExcluded: true as const } : {}),
    tiles: OVERVIEW_LAYOUT.flatMap((id): LookerChatTile[] => {
      const widget = APP_BOX[id];
      const tile = widget
        ? appChatTile({ title: input.appTitles[widget], type: 'app' }, input.app[widget], input.coverage)
        : (() => {
          const b = input.tables.find((x) => x.id === id);
          return b ? chatTileOf(b, currency) : null;
        })();
      return tile ? [tile] : [];
    }),
  };
};
